import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Close, DragIndicator } from "@mui/icons-material";
import * as FantasyService from "../../../services/FantasyService";
import { getPlayersByTeamId } from "../../../services/PlayerService";
import { FantasyPlayer } from "../../../models/FantasyPlayer";
import { Standing } from "../../../models/Standing";
import { hasGamesPlayed } from "../../../utils/predictionScore";
import RosterStrip from "./RosterStrip";
import {
  NEW_PLAYER_THRESHOLD,
  Slots,
  deviationClass,
  divisionLabel,
  legendFor,
  zoneColor,
} from "./predictShared";
import styles from "../Predict.module.css";

export interface LastSeasonStanding {
  rank: number;
  division: string;
}

interface DivisionPredictorProps {
  season: number;
  division: string;
  /** The division's current standings: its teams, and their rank now. */
  standings: Standing[];
  /** Previous season's finish per team id. */
  lastSeason: Record<string, LastSeasonStanding>;
  slots: Slots;
  onSlotsChange: (slots: Slots) => void;
  /** Teams can be moved. */
  editable: boolean;
  /** Read-only view of the saved pick against the table now. */
  locked: boolean;
  canUndo: boolean;
  onUndo: () => void;
}

interface TeamInfo {
  teamId: string;
  teamName: string;
  teamLogo: string;
  currentRank: number;
  avgElo: number;
  players: FantasyPlayer[];
  last?: LastSeasonStanding;
  /** Current players who didn't play for the team last season (known once loaded). */
  newPlayers: string[];
}

type DragSource = { teamId: string; from: "pool" | "slot" };

const TeamLogo: React.FC<{ team: TeamInfo; className?: string; fallbackClassName?: string }> = ({
  team,
  className = styles.logo,
  fallbackClassName = styles.logoFallback,
}) =>
  team.teamLogo ? (
    <img className={className} src={team.teamLogo} alt="" draggable={false} />
  ) : (
    <span className={fallbackClassName}>{team.teamName.charAt(0).toUpperCase()}</span>
  );

/**
 * One division's prediction board, the same interaction as before the redesign: drag a team from
 * the group onto a place (the occupant goes back to the group), drag between places to swap. New:
 * drag a team back to the group or press its cross to take it out, and click a team then a place
 * to do any of it without a mouse. On phones the group becomes chips and a tap fills the next place.
 */
const DivisionPredictor: React.FC<DivisionPredictorProps> = ({
  season,
  division,
  standings,
  lastSeason,
  slots,
  onSlotsChange,
  editable,
  locked,
  canUndo,
  onUndo,
}) => {
  const { t } = useTranslation("pages");
  const [picked, setPicked] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragSource | null>(null);
  // The drag handlers read this, not `drag`: the first dragover can fire before React has rendered
  // the dragstart's state, and a dragover that isn't accepted cancels the drop. State is for styling.
  const dragRef = useRef<DragSource | null>(null);
  const [over, setOver] = useState<number | "pool" | null>(null);
  const [rosters, setRosters] = useState<Record<string, FantasyPlayer[]>>({});
  const [rostersProcessing, setRostersProcessing] = useState(false);
  const [lastRosters, setLastRosters] = useState<Record<string, Set<string>>>({});

  // Rosters and ELO come from the fantasy cache. They only decorate the board: the teams themselves
  // come from the standings, so a cold cache no longer leaves the group empty.
  useEffect(() => {
    let cancelled = false;
    setPicked(null);
    setFocused(null);
    setRosters({});
    setRostersProcessing(false);
    FantasyService.getAllTeams(season, division)
      .then((teams) => {
        if (cancelled) return;
        const byTeam: Record<string, FantasyPlayer[]> = {};
        for (const team of teams) byTeam[String(team.id)] = team.players ?? [];
        setRosters(byTeam);
      })
      .catch((err) => {
        if (!cancelled && err?.isProcessing) setRostersProcessing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [season, division]);

  // Who played for each team last season (from the match data), to count the new faces on the roster.
  // Only teams that finished last season under the same id have something to compare against.
  const lastSeasonTeamIds = standings
    .map((s) => s.team.teamId)
    .filter((id) => lastSeason[id])
    .join(",");
  useEffect(() => {
    let cancelled = false;
    setLastRosters({});
    const ids = lastSeasonTeamIds ? lastSeasonTeamIds.split(",") : [];
    Promise.all(
      ids.map((id) =>
        getPlayersByTeamId(id, season - 1)
          .then((players) => [id, new Set(players.map((p) => p.steamID))] as const)
          .catch(() => null)
      )
    ).then((entries) => {
      if (cancelled) return;
      const byTeam: Record<string, Set<string>> = {};
      for (const entry of entries) if (entry) byTeam[entry[0]] = entry[1];
      setLastRosters(byTeam);
    });
    return () => {
      cancelled = true;
    };
  }, [season, lastSeasonTeamIds]);

  const teams = useMemo(() => {
    const map = new Map<string, TeamInfo>();
    for (const s of standings) {
      const players = rosters[s.team.teamId] ?? [];
      const played = lastRosters[s.team.teamId];
      const newPlayers = played
        ? players.filter((p) => !played.has(p.account.steamID)).map((p) => p.account.playerName)
        : [];
      const rated = players.filter((p) => p.account?.faceitElo);
      const avgElo = rated.length
        ? Math.round(rated.reduce((sum, p) => sum + p.account.faceitElo, 0) / rated.length)
        : 0;
      map.set(s.team.teamId, {
        teamId: s.team.teamId,
        teamName: s.team.teamName,
        teamLogo: s.team.teamLogo,
        currentRank: s.rank,
        avgElo,
        players,
        last: lastSeason[s.team.teamId],
        newPlayers,
      });
    }
    return map;
  }, [standings, rosters, lastRosters, lastSeason]);

  // Same as before the redesign: mean of the team averages, a team without rated players counts as 0.
  const groupElo = useMemo(() => {
    const list = Array.from(teams.values());
    const total = list.reduce((sum, team) => sum + team.avgElo, 0);
    return list.length && total ? Math.round(total / list.length) : 0;
  }, [teams]);

  const pool = useMemo(
    () =>
      Array.from(teams.values())
        .filter((team) => !slots.includes(team.teamId))
        .sort((a, b) => b.avgElo - a.avgElo || a.teamName.localeCompare(b.teamName)),
    [teams, slots]
  );

  const scored = hasGamesPlayed(standings);
  const placedCount = slots.filter(Boolean).length;
  const legend = legendFor(season, division, standings.length);
  const pickedTeam = picked ? teams.get(picked) : undefined;
  const focusedTeam = focused ? teams.get(focused) : undefined;

  const place = (teamId: string, index: number) => {
    const next = [...slots];
    const from = next.indexOf(teamId);
    if (from === index) {
      setPicked(null);
      return;
    }
    const occupant = next[index];
    next[index] = teamId;
    // From another place the two swap; from the group the occupant simply falls back into it.
    if (from >= 0) next[from] = occupant;
    onSlotsChange(next);
    setPicked(null);
    setFocused(teamId);
  };

  const unplace = (teamId: string) => {
    onSlotsChange(slots.map((id) => (id === teamId ? null : id)));
    setPicked(null);
  };

  const placeInNextFree = (teamId: string) => {
    const index = slots.indexOf(null);
    if (index >= 0) place(teamId, index);
  };

  const togglePick = (teamId: string) => {
    setFocused(teamId);
    if (editable) setPicked((current) => (current === teamId ? null : teamId));
  };

  const endDrag = () => {
    dragRef.current = null;
    setDrag(null);
    setOver(null);
  };

  const startDrag = (teamId: string, from: DragSource["from"]) => (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = "move";
    // Firefox won't start a drag without data; the payload itself lives in dragRef.
    e.dataTransfer.setData("text/plain", teamId);
    dragRef.current = { teamId, from };
    setDrag({ teamId, from });
    setFocused(teamId);
  };

  const slotDragOver = (index: number) => (e: React.DragEvent) => {
    if (!dragRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (over !== index) setOver(index);
  };

  const slotDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const source = dragRef.current;
    if (source) place(source.teamId, index);
    endDrag();
  };

  const poolDragOver = (e: React.DragEvent) => {
    if (dragRef.current?.from !== "slot") return;
    e.preventDefault();
    if (over !== "pool") setOver("pool");
  };

  const poolDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const source = dragRef.current;
    if (source?.from === "slot") unplace(source.teamId);
    endDrag();
  };

  const leaveArea = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(null);
  };

  const lastSeasonLine = (team: TeamInfo) =>
    team.last
      ? t("predict.lastSeason", { rank: team.last.rank, division: divisionLabel(team.last.division) })
      : t("predict.newThisSeason", { season });
  const hint = locked
    ? t("predict.hintLocked")
    : pickedTeam
      ? t("predict.hintPick", { team: pickedTeam.teamName })
      : t("predict.hintDrag");

  const poolClass = [
    styles.panel,
    styles.pool,
    drag?.from === "slot" ? styles.poolDropTarget : "",
    over === "pool" ? styles.poolDropOver : "",
  ].join(" ");

  return (
    <>
      <div className={styles.editorGrid}>
        <section aria-label={t("predict.teamsInGroup")} className={poolClass} onDragOver={poolDragOver} onDrop={poolDrop} onDragLeave={leaveArea}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>{t("predict.teamsInGroup")}</h2>
            <span className={styles.spacer} />
            {!!groupElo && <span className={styles.panelMeta}>{t("predict.avgElo", { elo: groupElo })}</span>}
          </div>

          {locked ? (
            <div className={styles.poolEmpty}>
              <span className={styles.poolEmptyTitle}>{t("predict.lockedPoolTitle")}</span>
              <span className={styles.poolEmptyText}>{t("predict.lockedPoolText")}</span>
            </div>
          ) : pool.length === 0 ? (
            <div className={styles.poolEmpty}>
              <span className={styles.poolEmptyTitle}>{t("predict.allPlaced")}</span>
              <span className={styles.poolEmptyText}>{t("predict.allPlacedHint")}</span>
            </div>
          ) : (
            <>
              <div className={styles.poolList}>
                <div className={`${styles.colHead} ${styles.poolCols}`}>
                  <span>{t("predict.teamsLeft", { count: pool.length })}</span>
                  <span className={styles.right}>{t("predict.elo")}</span>
                </div>
                {pool.map((team) => (
                  <div
                    key={team.teamId}
                    className={[
                      styles.poolRow,
                      picked === team.teamId ? styles.picked : "",
                      drag?.teamId === team.teamId ? styles.dragging : "",
                    ].join(" ")}
                    draggable={editable}
                    onDragStart={editable ? startDrag(team.teamId, "pool") : undefined}
                    onDragEnd={endDrag}
                  >
                    <div className={styles.teamCell}>
                      {editable && (
                        <span className={styles.grip} title={t("predict.dragToSlot")}>
                          <DragIndicator sx={{ fontSize: 18 }} />
                        </span>
                      )}
                      <button
                        type="button"
                        className={styles.teamButton}
                        aria-pressed={picked === team.teamId}
                        onClick={() => togglePick(team.teamId)}
                      >
                        <TeamLogo team={team} />
                        <span className={styles.teamName}>{team.teamName}</span>
                      </button>
                    </div>
                    <span className={styles.num}>{team.avgElo || "–"}</span>
                  </div>
                ))}
              </div>
              <div className={styles.poolChips}>
                <span className={styles.panelMeta} style={{ flexBasis: "100%" }}>{t("predict.tapInOrder")}</span>
                {pool.map((team) => (
                  <button
                    key={team.teamId}
                    type="button"
                    className={styles.poolChip}
                    disabled={!editable}
                    onClick={() => placeInNextFree(team.teamId)}
                  >
                    <TeamLogo team={team} />
                    <span className={styles.poolChipName}>{team.teamName}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>

        <section aria-label={t("predict.yourRanking")} className={`${styles.panel} ${styles.slotsPanel}`} onDragLeave={leaveArea}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>{t("predict.yourRanking")}</h2>
            <span className={styles.spacer} />
            {editable && (
              <div className={styles.slotActions}>
                <button type="button" className={styles.ghostButton} onClick={onUndo} disabled={!canUndo}>
                  {t("predict.undo")}
                </button>
                <button
                  type="button"
                  className={styles.ghostButton}
                  onClick={() => onSlotsChange(slots.map(() => null))}
                  disabled={placedCount === 0}
                >
                  {t("predict.clear")}
                </button>
              </div>
            )}
          </div>

          <div className={`${styles.colHead} ${styles.slotCols}`}>
            <span className={styles.center}>#</span>
            <span style={{ paddingLeft: 12 }}>{t("predict.team")}</span>
            <span className={styles.right}>{locked ? t("predict.now") : t("predict.elo")}</span>
            <span className={styles.right}>{locked && scored ? t("predict.deviation") : ""}</span>
          </div>

          {slots.map((teamId, index) => {
            const team = teamId ? teams.get(teamId) : undefined;
            const deviation = team ? Math.abs(team.currentRank - (index + 1)) : 0;
            return (
              <div
                key={index}
                className={[
                  styles.slotRow,
                  index % 2 === 1 ? styles.slotRowAlt : "",
                  team && picked === team.teamId ? styles.picked : "",
                  over === index && drag ? styles.slotOver : "",
                  team && drag?.teamId === team.teamId && drag.from === "slot" ? styles.dragging : "",
                ].join(" ")}
                draggable={editable && !!team}
                onDragStart={editable && team ? startDrag(team.teamId, "slot") : undefined}
                onDragOver={editable ? slotDragOver(index) : undefined}
                onDrop={editable ? slotDrop(index) : undefined}
                onDragEnd={endDrag}
              >
                <span className={styles.rankCell} style={{ background: zoneColor(season, division, index) }}>
                  {index + 1}
                </span>

                {team ? (
                  <div className={styles.slotTeam}>
                    {editable && (
                      <span className={styles.grip} title={t("predict.dragToSlot")}>
                        <DragIndicator sx={{ fontSize: 18 }} />
                      </span>
                    )}
                    <button
                      type="button"
                      className={styles.teamButton}
                      aria-pressed={picked === team.teamId}
                      onClick={() => {
                        if (editable && picked && picked !== team.teamId) place(picked, index);
                        else togglePick(team.teamId);
                      }}
                    >
                      <TeamLogo team={team} />
                      <span className={styles.teamName}>{team.teamName}</span>
                    </button>
                  </div>
                ) : (
                  <div className={styles.emptyCell}>
                    <button
                      type="button"
                      className={`${styles.emptySlot} ${pickedTeam ? styles.emptySlotArmed : ""}`}
                      disabled={!editable}
                      onClick={() => picked && place(picked, index)}
                    >
                      {locked
                        ? t("predict.noPrediction")
                        : pickedTeam
                          ? t("predict.placeHere")
                          : t("predict.dropTeamHere")}
                    </button>
                  </div>
                )}

                <span className={styles.num}>
                  {team ? (locked ? (scored ? `${team.currentRank}.` : "–") : team.avgElo || "–") : ""}
                </span>

                <span className={styles.endCell}>
                  {team && editable && (
                    <button
                      type="button"
                      className={styles.iconButton}
                      title={t("predict.backToGroup")}
                      aria-label={t("predict.remove", { team: team.teamName, rank: index + 1 })}
                      onClick={() => unplace(team.teamId)}
                    >
                      <Close sx={{ fontSize: 16 }} />
                    </button>
                  )}
                  {team && locked && scored && (
                    <span className={`${styles.devChip} ${deviationClass(deviation)}`}>{deviation}</span>
                  )}
                </span>
              </div>
            );
          })}

          <div className={styles.panelFoot}>
            <div className={styles.legend}>
              {legend.map((item) => (
                <span key={item.zone} className={styles.legendItem}>
                  <span className={styles.legendSwatch} style={{ background: item.color }} />
                  {t(item.labelKey)}
                </span>
              ))}
            </div>
            <span className={styles.progressText}>
              {t("predict.statusPlaced", { placed: placedCount, total: standings.length })}
            </span>
          </div>
          <div className={styles.progress} aria-hidden>
            <div
              className={styles.progressFill}
              style={{ width: `${standings.length ? Math.round((placedCount / standings.length) * 100) : 0}%` }}
            />
          </div>
        </section>

        <aside aria-label={focusedTeam?.teamName ?? t("predict.clickTeamDetails")} className={`${styles.panel} ${styles.details}`}>
          {focusedTeam ? (
            <>
              <div className={styles.detailsTop}>
                <TeamLogo team={focusedTeam} className={styles.detailsLogo} fallbackClassName={styles.detailsLogoFallback} />
                <span className={styles.detailsName}>{focusedTeam.teamName}</span>
                <span className={styles.detailsMeta}>{lastSeasonLine(focusedTeam)}</span>
                {focusedTeam.last && focusedTeam.newPlayers.length > NEW_PLAYER_THRESHOLD && (
                  <span
                    className={styles.detailsChange}
                    title={t("predict.newPlayersTitle", {
                      count: focusedTeam.newPlayers.length,
                      names: focusedTeam.newPlayers.join(", "),
                    })}
                  >
                    * {t("predict.newPlayersLine", { count: focusedTeam.newPlayers.length })}
                  </span>
                )}
              </div>
              <div className={styles.statGrid}>
                {locked ? (
                  <>
                    <div className={styles.statBox}>
                      <div className={styles.statLabel}>{t("predict.yourPick")}</div>
                      <div className={styles.statValue}>
                        {slots.includes(focusedTeam.teamId) ? `${slots.indexOf(focusedTeam.teamId) + 1}.` : "–"}
                      </div>
                    </div>
                    <div className={styles.statBox}>
                      <div className={styles.statLabel}>{t("predict.now")}</div>
                      <div className={styles.statValue}>{scored ? `${focusedTeam.currentRank}.` : "–"}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.statBox}>
                      <div className={styles.statLabel}>{t("predict.elo")}</div>
                      <div className={styles.statValue}>{focusedTeam.avgElo || "–"}</div>
                    </div>
                    <div className={styles.statBox}>
                      <div className={styles.statLabel}>{t("predict.yourPlace")}</div>
                      <div className={styles.statValue}>
                        {slots.includes(focusedTeam.teamId) ? `${slots.indexOf(focusedTeam.teamId) + 1}.` : "–"}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className={styles.detailsEmpty}>{t("predict.clickTeamDetails")}</div>
          )}
        </aside>
      </div>

      {focusedTeam && focusedTeam.players.length > 0 && (
        <RosterStrip
          teamId={focusedTeam.teamId}
          teamName={focusedTeam.teamName}
          teamLogo={focusedTeam.teamLogo}
          avgElo={focusedTeam.avgElo}
          players={focusedTeam.players}
        />
      )}

      {rostersProcessing && <div className={`${styles.note} ${styles.noteWarn}`}>{t("predict.fantasyLoading")}</div>}
      <p className={styles.hint}>{hint}</p>
    </>
  );
};

export default DivisionPredictor;
